import { Box, LinearProgress, Typography } from '@mui/material';

import { SCORE_THRESHOLDS, scoreColor, scoreLabel, tokens } from '../../../theme-next';

/**
 * Composants visuels premium partagés par l'écran récap agence et le
 * dashboard GOD review (à venir au Chantier 5). Ne touchent pas aux
 * composants du design system global (ScoreBar, ConfidenceRow, Chip) —
 * on garde cette variante "chiadée" locale aux écrans CG IA.
 */

export interface PremiumRowData {
  label: string;
  value: string;
  confidencePct: number; // 0-100
}

export const StatusPill = ({ score }: { score: number }) => {
  const color = scoreColor(score);
  const label = scoreLabel(score);
  const bg =
    score >= SCORE_THRESHOLDS.GREEN
      ? tokens.color.greenLight
      : score >= SCORE_THRESHOLDS.ORANGE
        ? tokens.color.orangeLight
        : tokens.color.redLight;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 12px',
        borderRadius: `${tokens.radius.full}px`,
        backgroundColor: bg,
        border: `1.5px solid ${color}`,
        color,
        fontSize: '12px',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
      {label}
    </Box>
  );
};

export const LargeScoreBar = ({ score }: { score: number }) => {
  const color = scoreColor(score);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 160 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={score}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: '#F0F0EE',
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 3,
            },
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 800,
          color,
          minWidth: '42px',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {score}%
      </Typography>
    </Box>
  );
};

export const PremiumRow = ({ row }: { row: PremiumRowData }) => {
  const needsReview = row.confidencePct > 0 && row.confidencePct < SCORE_THRESHOLDS.GREEN;
  const isEmpty = row.value === '—' || row.confidencePct === 0;
  const color = scoreColor(row.confidencePct);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 220px) 1fr auto' },
        alignItems: 'center',
        gap: '16px',
        padding: '18px 22px 18px 24px',
        borderBottom: `1px solid ${tokens.color.border}`,
        position: 'relative',
        transition: 'background-color 150ms ease',
        '&::before': needsReview
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              backgroundColor: color,
            }
          : {},
        backgroundColor: needsReview ? '#FFFDF5' : 'transparent',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: needsReview ? color : tokens.color.textMuted,
          lineHeight: 1.3,
        }}
      >
        {row.label}
      </Typography>
      <Typography
        sx={{
          fontSize: '17px',
          fontWeight: 700,
          color: isEmpty ? tokens.color.textFaint : tokens.color.dark,
          fontStyle: isEmpty ? 'italic' : 'normal',
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
      >
        {row.value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
        <LargeScoreBar score={row.confidencePct} />
        <StatusPill score={row.confidencePct} />
      </Box>
    </Box>
  );
};

export const SectionCard = ({
  title,
  subtitle,
  subtitleStrong,
  accentColor,
  headerRight,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Quand true, le subtitle est en gras + couleur dark (call-to-action). */
  subtitleStrong?: boolean;
  accentColor?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      backgroundColor: tokens.color.white,
      border: `1px solid ${tokens.color.border}`,
      borderRadius: `${tokens.radius['3xl']}px`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        padding: '22px 26px',
        borderBottom: `1px solid ${tokens.color.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
        {accentColor && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: accentColor,
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 800,
              color: tokens.color.dark,
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: subtitleStrong ? '14px' : '13px',
                fontWeight: subtitleStrong ? 700 : 500,
                color: tokens.color.dark,
                marginTop: subtitleStrong ? '4px' : '2px',
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {headerRight}
    </Box>
    {children}
  </Box>
);
